import { FunctionComponent, useRef } from "react";
import { ConnectedProps, connect } from "react-redux";
import { getActiveSite } from "../../selectors/sites.js";
import {
  BaseModuleProps,
  PriceModuleSettings,
  PricesWidgetSettings,
  StoreState,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getSupportedLanguage,
} from "../../utils/utils.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";
import PricesWidget from "../PricesWidget.js";
import RichEditorWrapper from "../RichEditorWrapper.js";

interface Props extends BaseModuleProps<PriceModuleSettings> {}

type ReduxProps = ConnectedProps<typeof connector>;

const PriceModule: FunctionComponent<Props & ReduxProps> = ({
  translatedModule,
  translatedModule: {
    id: moduleId,
    settings: { textAlign },
    translation: {
      settings: { title, subtitle },
      languageId,
    },
  },
  scheme,
  pageId,
  isPreview,
  isFirstOnPage,
  settings,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <ModuleWithHeadings
      innerRef={ref}
      title={title}
      subtitle={subtitle}
      id={moduleId}
      className={`PriceModule lazyload`}
      colors={{ background: scheme.main.separator, color: scheme.main.text }}
    >
      <div className="Module__Wrapper">
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />

        <PricesWidget settings={settings} />

        <RichEditorWrapper
          module={translatedModule}
          pageId={pageId}
          languageId={languageId}
          isPreview={isPreview}
          scheme={scheme}
          className={`PriceModule__AdditionalInfos TextAlign--${textAlign.description}`}
        />
      </div>
    </ModuleWithHeadings>
  );
};

const mapStateToProps = (
  { colorSchemes, sites }: StoreState,
  { translatedModule }: Props,
) => {
  const site = getActiveSite(sites);

  const {
    translation: { languageId },
    settings: { grouping, layout, roomIds, offerIds, boardIds },
  } = translatedModule;

  const settings: PricesWidgetSettings = {
    propertyId: site.accommodation.id,
    lang: getSupportedLanguage(languageId, ["de", "it", "en", "fr"]),
    colorScheme: getActiveColorScheme(colorSchemes, site, translatedModule),
    grouping,
    layout,
    roomIds: roomIds.length ? roomIds : undefined,
    offerIds: offerIds.length ? offerIds : undefined,
    boardIds: boardIds.length ? boardIds : undefined,
  };

  return {
    scheme: getActiveColorScheme(colorSchemes, site, translatedModule),
    settings,
  };
};

const connector = connect(mapStateToProps);

export default connector(PriceModule);
